/**
 * CommunityService
 *
 * Domain bridge for the community wall. Calls the Task-3 `firestoreService`
 * primitives and delegates the full data <-> domain mapping to
 * `CommunityChatMessageRemapper` (`fromJson` for JSON -> DTO, `toEntity` for
 * DTO -> entity, `toDtoFromEntity` + `toJson` for entity -> JSON). The remapper
 * owns the absolute media/reply download-URL derivation Flutter exposes via its
 * `get path` / `get thumbnail` getters.
 *
 * Architecture (conventions.md § Clean Architecture flow):
 *   UseCase -> CommunityService -> FirestoreService
 * The data layer never imports a remapper; the DTO <-> entity mapping lives in
 * the remappers and the service orchestrates it.
 *
 * Mirrors `CommunityRepository` / `CommunityDataSource`:
 *   lib/data/repository/community_repository.dart:9-25
 *   lib/data/data_sources/community_data_source.dart:46-165
 *
 * Singleton (`getInstance()`), matching `ChatService`
 * (chatService.ts:35-45).
 *
 * Scope: the in-scope reads/writes (paginated fetch, live subscribe, add
 * message, like message) plus the image-media send halves (`uploadCommunityMedia`
 * raw upload + `transcodeCommunityImage` callable fire — the two ends of Flutter's
 * `_uploadCommunityMessageMedia`, sequenced by `SendCommunityImageMessageUseCase`
 * so the message doc write interleaves between them) and the video-media send
 * halves (`uploadCommunityVideo` raw upload + plaintext thumbnail capture/upload
 * + `transcodeCommunityVideo` callable fire — the community mirror of the Chat
 * video pipeline, sequenced by `SendCommunityVideoMessageUseCase`, Task 12).
 */

import type { Unsubscribe } from 'firebase/firestore';
import { firestoreService } from '@/data/services/firestoreService';
import { cloudStorageService } from '@/data/storage/remote/cloudStorageService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { CommunityChatMessageRemapper } from '@/domain/remappers/community/communityChatMessageRemapper';
import { CommunityMessageLikeRemapper } from '@/domain/remappers/communityMessageLike/communityMessageLikeRemapper';
import { buildUploadFileInfo } from '@/domain/utils/uploadFileInfoBuilder';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MediaType } from '@/domain/enums/mediaType';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { prepareCommunityVideoThumbnailUseCase } from '@/domain/usecases/community/prepareCommunityVideoThumbnailUseCase';
import type { JsonObject } from '@/data/types/json';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { CommunityMessageLikeDto } from '@/data/dtos/communityMessageLike/communityMessageLikeDto';

/** Per-change live-listener callbacks, keyed on `RemoteDocumentChangeType`. */
export interface CommunityMessageChangeHandlers {
  onAdded?: (message: CommunityChatMessageEntity) => void;
  onModified?: (message: CommunityChatMessageEntity) => void;
  onRemoved?: (message: CommunityChatMessageEntity) => void;
}

export class CommunityService {
  private static instance: CommunityService;

  private constructor() {}

  static getInstance(): CommunityService {
    if (!this.instance) {
      this.instance = new CommunityService();
    }
    return this.instance;
  }

  /**
   * Fetch a newest-first page of community-wall messages for `owner`.
   *
   * Proxies `firestoreService.fetchCommunityMessagesPage`, remaps each raw
   * `JsonObject` to a DTO via `CommunityChatMessageRemapper.fromJson`, then maps
   * DTO -> entity.
   *
   * `hasMore` is derived here with strict `=== limit` — parity with Flutter
   * `response.hasMore = querySnapshot.docs.length == limit`
   * (community_data_source.dart:74). The data layer leaves this derivation to
   * the caller (firestoreService.ts:1442-1444).
   *
   * @param owner - The creator's firebaseId whose wall is read.
   * @param lastTimestamp - The previous page's tail `createdTimestamp`, or
   *   `null` for the first page.
   * @param limit - Page size.
   */
  async fetchMessagesPage(
    owner: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<{
    messages: CommunityChatMessageEntity[];
    lastTimestamp: number | null;
    hasMore: boolean;
  }> {
    const { messages: rawMessages, lastTimestamp: nextLastTimestamp } =
      await firestoreService.fetchCommunityMessagesPage(owner, lastTimestamp, limit);

    const dtos = rawMessages.map(CommunityChatMessageRemapper.fromJson);
    const messages = dtos.map(CommunityChatMessageRemapper.toEntity);

    return {
      messages,
      lastTimestamp: nextLastTimestamp,
      // Strict `=== limit` (NOT `>=`), parity with community_data_source.dart:74.
      hasMore: dtos.length === limit,
    };
  }

  /**
   * Subscribe to live community-wall message changes for `owner` arriving after
   * `sinceTimestamp`, remapping each change's raw doc to a
   * `CommunityChatMessageEntity` and dispatching to the matching callback keyed
   * on `RemoteDocumentChangeType` — mirroring how `ChatService` feeds the chat
   * live listener (chatService.ts:451-471).
   *
   * @returns An `Unsubscribe` handle the caller is responsible for invoking.
   */
  subscribeToMessages(
    owner: string,
    sinceTimestamp: number,
    handlers: CommunityMessageChangeHandlers,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToCommunityMessages(
      owner,
      sinceTimestamp,
      (rawChanges) => {
        for (const change of rawChanges) {
          const entity = CommunityChatMessageRemapper.toEntity(
            CommunityChatMessageRemapper.fromJson(change.data),
          );
          switch (change.type) {
            case RemoteDocumentChangeType.added:
              handlers.onAdded?.(entity);
              break;
            case RemoteDocumentChangeType.modified:
              handlers.onModified?.(entity);
              break;
            case RemoteDocumentChangeType.removed:
              handlers.onRemoved?.(entity);
              break;
          }
        }
      },
      onError,
    );
  }

  /**
   * Add a single community-wall message.
   *
   * Bridges entity -> DTO -> `CommunityChatMessageRemapper.toJson`, then calls
   * `firestoreService.addCommunityMessage(owner, message.id, messageData)`. The
   * owner is `message.messageUsersInfo.owner` — parity with Flutter using
   * `message.messageUsersInfo.owner` as the write target
   * (community_data_source.dart:135-139).
   *
   * @returns `true` on success, `false` on failure (parity with Flutter's
   *   `bool success` pattern, community_data_source.dart:133/158/161).
   */
  async addMessage(message: CommunityChatMessageEntity): Promise<boolean> {
    const dto = CommunityChatMessageRemapper.toDtoFromEntity(message);
    const messageData = CommunityChatMessageRemapper.toJson(dto);
    return firestoreService.addCommunityMessage(
      message.messageUsersInfo.owner,
      message.id,
      messageData,
    );
  }

  /**
   * Like or unlike a community-wall message.
   *
   * Serializes the like DTO to its full Firestore document shape via
   * `CommunityMessageLikeRemapper.toJson` and calls
   * `firestoreService.likeCommunityMessage` (like doc set-merge + message
   * `likes` increment). The payload is the complete serialized like — parity
   * with Flutter `var messageLike = like.toJson();`
   * (community_data_source.dart:25). This method is the repository -> data
   * source hop (content_reactions_repository.dart:98); the optimistic
   * local-storage write + revert-on-failure lives in the use case (Task 3).
   *
   * The DTO is the natural currency here (mirrors `CommentService.likeComment`
   * taking `CommentLikeDto`, commentService.ts:117-122) — there is deliberately
   * no `CommunityMessageLike` entity on web; its storage shape
   * (`CommunityMessageLikeStorage`) and DTO are field-identical.
   *
   * @returns `true` on success, `false` on failure (parity with Flutter's
   *   `bool success` pattern, community_data_source.dart:10/37/40/43).
   */
  async likeMessage(like: CommunityMessageLikeDto): Promise<boolean> {
    return firestoreService.likeCommunityMessage({
      userId: like.userId,
      communityMessageId: like.communityMessageId,
      liked: like.liked,
      likeData: CommunityMessageLikeRemapper.toJson(like),
    });
  }

  /**
   * Upload a community-message image file to the default Firebase Storage bucket
   * and return the upload path + file extension the transcode payload needs.
   *
   * This is the raw-upload half of Flutter's
   * `AppStorageService._uploadCommunityMessageMedia`
   * (lib/services/app_storage_service.dart:216-266): build `uploadInfo`, upload
   * to `community/{communityOwner}/{timestampMillis}`, and on any thrown upload
   * error catch + log + return the not-uploaded result (parity with the
   * `catch (error) { print(...); return false; }` at
   * app_storage_service.dart:262-265). The transcode callable is intentionally
   * NOT fired here — it is `transcodeCommunityImage`'s job, so the use case
   * (`SendCommunityImageMessageUseCase`) can interleave the message-doc write
   * between the two (upload → `addMessage` → this fire; task-prompt line 19).
   *
   * `uploadInfo` is built via `buildUploadFileInfo`. Its `uploadFilePath` field
   * (the uploader's firebaseId) is UNUSED for community uploads —
   * `MediaUtils.getCommunityContentUploadFilePath` derives the path from
   * `communityOwner` (the wall owner), not from `uploadFilePath`. For a JPEG
   * input the source-format-preserving content-type resolution (the established
   * web divergence documented at uploadFileInfoBuilder.ts:44-48) yields exactly
   * Flutter's `image/jpeg` / `jpg` (video_process_service.dart:17-20). Duration
   * is `0` (images have no duration).
   *
   * @param args.file - The image file to upload.
   * @param args.communityOwner - The wall owner (`message.messageUsersInfo.owner`)
   *   whose `community/{owner}/…` storage path the file lands under, matching the
   *   Firestore write target (community_data_source.dart:135-139).
   * @returns `{ uploaded, uploadPath, fileExtension }` — `uploaded` is `false`
   *   when the storage upload throws (parity with app_storage_service.dart:262-265);
   *   `uploadPath` / `fileExtension` are returned in both cases so the caller can
   *   forward them to `transcodeCommunityImage` on success.
   */
  async uploadCommunityMedia(args: {
    file: File;
    communityOwner: string;
  }): Promise<{ uploaded: boolean; uploadPath: string; fileExtension: string }> {
    const { file, communityOwner } = args;

    const uploadInfo = buildUploadFileInfo({
      file,
      firebaseUserId: communityOwner,
      durationSeconds: 0,
    });
    const uploadPath = MediaUtils.getCommunityContentUploadFilePath(uploadInfo, communityOwner);

    try {
      await cloudStorageService.uploadCommunityMediaContent(
        file,
        uploadPath,
        uploadInfo.contentType,
      );
      return { uploaded: true, uploadPath, fileExtension: uploadInfo.fileExtension };
    } catch (error) {
      appLogger.error(
        'Error uploading community media',
        'CommunityService.uploadCommunityMedia',
        { error: error as Error },
      );
      return { uploaded: false, uploadPath, fileExtension: uploadInfo.fileExtension };
    }
  }

  /**
   * Fire the `transcodeCommunityMessageImage` Cloud Function (fire-and-forget)
   * for a message whose image has already been uploaded and whose doc already
   * exists.
   *
   * This is the callable-fire half of Flutter's
   * `AppStorageService._uploadCommunityMessageMedia` — Flutter fires it
   * un-awaited after the upload (app_storage_service.dart:250-260), and the data
   * proxy (`cloudFunctionsService.transcodeCommunityMessageContent`) swallows +
   * logs its own errors, so this method returns synchronously and never rejects.
   *
   * The payload keys + image-flow values are byte-for-byte
   * `cloud_functions_service.dart:213-224`:
   *   - `description` / `duration` are `null` — never set in the community image
   *     flow (`user_media.dart:43,53` are nullable and unset by
   *     `add_content_screen.dart:456-479` `_createLocalMedia`).
   *   - `timeStartExpression` / `timeDurationExpression` are `null` — images are
   *     never trimmed (`preview_content_screen.dart:174-204` only sets crop
   *     expressions in the video branch).
   *   - `resizeWidth` / `resizeHeight` are `null` — only the on-device video-trim
   *     path sets them.
   *   - `aspectRatio` MUST be present (never omitted): the CF echoes it straight
   *     into the merge-written `mediaInfo.aspectRatio`
   *     (transcode_community_message_image.ts:169-181), so dropping it would
   *     corrupt the doc's `mediaInfo`.
   *
   * Dispatch is `MediaType.IMAGE` (the data proxy maps that to
   * `transcodeCommunityMessageImage`; the community-video callable is deferred).
   *
   * Caller-ordering contract (NOT enforced here — the use case sequences it):
   * upload → `addMessage` (doc write) → this fire. The web writes the doc BEFORE
   * firing because the CF locates it via
   * `collectionGroup('community_messages').where('id','==',messageId)`, so the
   * doc must exist first (task-prompt line 19).
   *
   * @param args.uploadPath - The `community/{owner}/{timestampMillis}` path from
   *   `uploadCommunityMedia` → payload `filePath`.
   * @param args.fileExtension - The upload's file extension → payload `fileExtension`.
   * @param args.aspectRatio - The image aspect ratio → payload `aspectRatio`
   *   (must be non-null for the CF merge-write; `null` only if genuinely unknown).
   * @param args.messageId - The already-written message doc id → payload `messageId`.
   */
  transcodeCommunityImage(args: {
    uploadPath: string;
    fileExtension: string;
    aspectRatio: number | null;
    messageId: string;
  }): void {
    const { uploadPath, fileExtension, aspectRatio, messageId } = args;

    const payload: JsonObject = {
      filePath: uploadPath,
      description: null,
      duration: null,
      fileExtension,
      aspectRatio,
      timeStartExpression: null,
      timeDurationExpression: null,
      resizeWidth: null,
      resizeHeight: null,
      messageId,
    };

    // Fire-and-forget (Flutter fires un-awaited, app_storage_service.dart:250-260;
    // the data proxy swallows + logs its own errors so this cannot reject).
    void cloudFunctionsService.transcodeCommunityMessageContent(payload, MediaType.IMAGE);
  }

  /**
   * Upload a community-message VIDEO to the default Firebase Storage bucket and
   * capture + upload its plaintext thumbnail, returning the upload path, file
   * extension, and tokenized download URL the transcode payload needs.
   *
   * This is the raw-upload half of the community mirror of Flutter's
   * `AppStorageService._uploadCommunityMessageMedia`
   * (lib/services/app_storage_service.dart:216-266), but built to follow the
   * CHAT video pipeline exactly (web chat template
   * `chatService.ts:312-336`) with Community-specific resources rather than
   * Flutter's legacy server-side ffmpeg path (sanctioned deviation, story_plan
   * `## Context`). The steps, in order:
   *   1. Upload the RAW video via `cloudStorageService.uploadCommunityVideoContent`
   *      (Task 9), reading back the tokenized download URL — unlike the community
   *      IMAGE upload, the video CF ffprobes this REMOTE URL for source metadata
   *      instead of downloading the whole file (chat precedent
   *      `transcode_chat_video.ts:66-74`). That URL is returned as `downloadUrl`
   *      so the caller can forward it to `transcodeCommunityVideo` as `dynamicUrl`.
   *   2. Capture the plaintext thumbnail via
   *      `PrepareCommunityVideoThumbnailUseCase.execute(file, uploadPath)`
   *      (Task 10). Community media is NOT E2EE (`useCommunity.ts:21`), so the
   *      frame is a plaintext JPEG — no encryption.
   *   3. When a frame was produced, upload the plaintext JPEG to its derived
   *      `thumbnailUploadPath` via the existing no-read-back
   *      `uploadCommunityMediaContent` with `'image/jpeg'`. On the codec fallback
   *      (`thumbnail === null`) SKIP the thumbnail upload — a missing thumbnail
   *      must NOT abort the send (mirror `chatService.ts:325-335`).
   *
   * Any thrown upload/prepare error → `appLogger.error` + the not-uploaded result
   * (parity with the `catch (error) { print(...); return false; }` at
   * app_storage_service.dart:262-265). The transcode callable is intentionally
   * NOT fired here — it is `transcodeCommunityVideo`'s job, so the use case
   * (`SendCommunityVideoMessageUseCase`, Task 12) can interleave the message-doc
   * write between the two (upload → `addMessage` → this fire).
   *
   * `uploadInfo` is built via `buildUploadFileInfo` with the sheet-captured
   * `durationSeconds`; its `uploadFilePath` field is UNUSED for community uploads
   * — `MediaUtils.getCommunityContentUploadFilePath` derives the 3-segment path
   * from `communityOwner` (the wall owner), matching the Firestore write target
   * (community_data_source.dart:135-139) and Flutter's
   * `community/{communityOwner}/{fileName}` (app_storage_service.dart:240-241).
   *
   * @param args.file - The video file to upload.
   * @param args.communityOwner - The wall owner (`message.messageUsersInfo.owner`)
   *   whose `community/{owner}/…` storage path the file lands under.
   * @param args.durationSeconds - The video duration (from the composer sheet),
   *   fed to `buildUploadFileInfo`.
   * @returns `{ uploaded, uploadPath, fileExtension, downloadUrl }` — `uploaded`
   *   is `false` when any upload/prepare step throws (parity with
   *   app_storage_service.dart:262-265); `uploadPath` / `fileExtension` are
   *   returned in both cases so the caller can forward them to
   *   `transcodeCommunityVideo` on success; `downloadUrl` is the raw video's
   *   tokenized URL (→ payload `dynamicUrl`), `null` when the raw upload never
   *   completed.
   */
  async uploadCommunityVideo(args: {
    file: File;
    communityOwner: string;
    durationSeconds: number;
  }): Promise<{
    uploaded: boolean;
    uploadPath: string;
    fileExtension: string;
    downloadUrl: string | null;
  }> {
    const { file, communityOwner, durationSeconds } = args;

    const uploadInfo = buildUploadFileInfo({
      file,
      firebaseUserId: communityOwner,
      durationSeconds,
    });
    const uploadPath = MediaUtils.getCommunityContentUploadFilePath(uploadInfo, communityOwner);

    let downloadUrl: string | null = null;
    try {
      // (1) Upload the RAW video (the Transcoder encrypts segments server-side),
      // resolving the download URL for the CF's remote-ffprobe `dynamicUrl`.
      const result = await cloudStorageService.uploadCommunityVideoContent(
        file,
        uploadPath,
        uploadInfo.contentType,
      );
      downloadUrl = result.downloadUrl;

      // (2) Capture the plaintext thumbnail. (3) Upload it when present — skip on
      // the codec fallback (a missing thumbnail must NOT abort the send).
      const preparedThumb = await prepareCommunityVideoThumbnailUseCase.execute(file, uploadPath);
      if (preparedThumb.thumbnail !== null && preparedThumb.thumbnailUploadPath !== null) {
        await cloudStorageService.uploadCommunityMediaContent(
          preparedThumb.thumbnail,
          preparedThumb.thumbnailUploadPath,
          'image/jpeg',
        );
      }

      return {
        uploaded: true,
        uploadPath,
        fileExtension: uploadInfo.fileExtension,
        downloadUrl,
      };
    } catch (error) {
      appLogger.error(
        'Error uploading community video',
        'CommunityService.uploadCommunityVideo',
        { error: error as Error },
      );
      return {
        uploaded: false,
        uploadPath,
        fileExtension: uploadInfo.fileExtension,
        downloadUrl,
      };
    }
  }

  /**
   * Fire the `transcodeCommunityMessageVideo` Cloud Function (fire-and-forget)
   * for a message whose video has already been uploaded and whose doc already
   * exists.
   *
   * This is the callable-fire half of the community video send. The data proxy
   * (`cloudFunctionsService.transcodeCommunityMessageContent`) dispatches
   * `mediaType !== 0` → `'transcodeCommunityMessageVideo'`
   * (cloudFunctionsService.ts:650-652; never invent the name) and swallows + logs
   * its own errors, so this method returns synchronously and never rejects.
   *
   * The payload keys mirror Flutter's `cloud_functions_service.dart:213-224` plus
   * the web-added `dynamicUrl` (chat precedent, `chatService.ts:356-367`):
   *   - `dynamicUrl` — the raw video's tokenized download URL from
   *     `uploadCommunityVideo`; the video CF ffprobes it REMOTELY (headers only)
   *     for source metadata. The community IMAGE flow has no raw upload, so its
   *     method omits this key.
   *   - `description` is `null` — never set in the community media flow
   *     (`user_media.dart` nullable + unset by `add_content_screen.dart:456-479`
   *     `_createLocalMedia`).
   *   - `duration` is the video duration in MILLISECONDS. Flutter's preview
   *     screen runs `_saveVideoTrim()` for every VIDEO media immediately before
   *     the callable fires and always assigns `widget.media.duration`
   *     (`preview_content_screen.dart:192-193`, untrimmed branch — the web has
   *     no on-device trim, so it is always the full duration); the payload sends
   *     `"duration": media.duration` (`cloud_functions_service.dart:216`). NOT
   *     null (that copy was the image sibling's — `_saveVideoTrim` no-ops for
   *     `type == 0`, so it is only true for images).
   *   - `timeStartExpression` / `timeDurationExpression` / `resizeWidth` /
   *     `resizeHeight` are `null` — the web has no on-device trim/crop, so no
   *     trim/resize expressions are produced (same as the image method).
   *   - `aspectRatio` is forwarded as-is for Flutter payload parity
   *     (`cloud_functions_service.dart:218`). The reworked video CF reads/logs it
   *     (`transcode_community_message_video.ts:84,88`) but NEVER writes it back —
   *     its only doc writes are the thumbnail field-write (`:156-161`) and the
   *     failure flip (`:193-195`), and the Task-5 completion write
   *     (`on_encrypt_community_video.ts:110`) sets only `mediaInfo.path` +
   *     `isUploading`. The persisted `mediaInfo.aspectRatio` is therefore the
   *     client's own optimistic doc write (remapper media branch; Flutter parity
   *     `medias_repository.dart:277`), NOT a CF echo.
   *
   * Dispatch is `MediaType.VIDEO`.
   *
   * Caller-ordering contract (NOT enforced here — the use case sequences it):
   * upload → `addMessage` (doc write) → this fire. The web writes the doc BEFORE
   * firing because the CF locates it via `where('id','==',messageId)` (Task 6),
   * so the doc must exist first — the same sanctioned reorder the image flow
   * documents (transcodeCommunityImage above).
   *
   * @param args.uploadPath - The `community/{owner}/{timestampMillis}` path from
   *   `uploadCommunityVideo` → payload `filePath`.
   * @param args.fileExtension - The upload's file extension → payload `fileExtension`.
   * @param args.aspectRatio - The video aspect ratio → payload `aspectRatio`.
   * @param args.messageId - The already-written message doc id → payload `messageId`.
   * @param args.dynamicUrl - The raw video's tokenized download URL from
   *   `uploadCommunityVideo` → payload `dynamicUrl` (the CF's remote ffprobe target).
   * @param args.durationMillis - The video duration in MILLISECONDS → payload
   *   `duration`. Flutter parity: `preview_content_screen.dart:192-193` (untrimmed
   *   `inMilliseconds`) → `cloud_functions_service.dart:216` (`"duration": media.duration`).
   */
  transcodeCommunityVideo(args: {
    uploadPath: string;
    fileExtension: string;
    aspectRatio: number | null;
    messageId: string;
    dynamicUrl: string | null;
    durationMillis: number;
  }): void {
    const { uploadPath, fileExtension, aspectRatio, messageId, dynamicUrl, durationMillis } = args;

    const payload: JsonObject = {
      filePath: uploadPath,
      dynamicUrl,
      description: null,
      duration: durationMillis,
      fileExtension,
      aspectRatio,
      timeStartExpression: null,
      timeDurationExpression: null,
      resizeWidth: null,
      resizeHeight: null,
      messageId,
    };

    // Fire-and-forget: the data proxy swallows + logs its own errors so this
    // cannot reject. Dispatch to the VIDEO callable (transcodeCommunityMessageVideo).
    void cloudFunctionsService.transcodeCommunityMessageContent(payload, MediaType.VIDEO);
  }
}

export const communityService = CommunityService.getInstance();
